import { JsEnv } from "../../models/js_env";
import { isClub } from "../../models/js_env";
import { ComparisonTableService } from "../../models/comparison_table/comparison_table_service";
import { ComparisonTableContainer } from "../../views/pc/comparison_table/comparison_table_container";
import { Env } from '../../../core/env';
import { TypedEvent } from "../../typed_event";
import { AdobeAnalyticsData } from "../../models/analytics/adobe_analytics_data";
import { ComparisonTableAnalytics } from "../analytics/comparison_table_analytics";
import { ComparisonTableAddAnalytics } from "../analytics/comparison_table_add_analytics";
import { ComparisonTableCheckAnalytics } from "../analytics/comparison_table_check_analytics";

/**
 * 比較表機能において、各画面に共通で必要な処理を担当するAction。
 */
export class ComparisonTableAction {
  static readonly SHOW_COMPARISON_MODAL_EVENT = new TypedEvent<AdobeAnalyticsData>();
  static readonly CHECK_COMPARISON_EVENT = new TypedEvent<AdobeAnalyticsData>();
  static readonly ADD_COMPARISON_EVENT = new TypedEvent<AdobeAnalyticsData>();

  /**
   * 比較表機能のための初期化処理。
   * @param env サーバサイドから連携されたデータ
   */
  static async init(env: JsEnv) {
    // 比較表機能はR1 CLUBのみ有効化する。
    if (!isClub(env.SITE_TYPE)) return;

    // Cookieはブラウザが閉じると消えるが、ローカルストレージは消えない。
    // そこで、画面初期表示時にローカルストレージから不要データを削除する。
    ComparisonTableService.clearLocalStorage();

    // 追従パーツ内の各商品の×ボタン(削除ボタン)のclickイベントにイベントハンドラを登録
    const deleteItemButtons = document.querySelectorAll('.js-bottomTrackingAddComparison_deleteBtn');
    deleteItemButtons.forEach((button) => {
      // 引数useCapture=trueとするのは、CXのイベントハンドラより先に実行するため。経緯: https://askul.backlog.jp/view/SALES_FLOOR-111
      button.addEventListener('click', ComparisonTableService.handleDeleteItemButtonClick.bind(ComparisonTableService), true);
    });

    // 比較表チェックボックスのchangeイベントについて、イベントハンドラを登録。
    // キーワード検索結果／中小カテトップの単品、商品詳細、バリエ一覧のエリアにあるチェックボックス
    const comparedItemCheckBoxes = document.querySelectorAll('.js-bottomTrackingAddComparison_checkbox input');
    comparedItemCheckBoxes.forEach(checkbox => {
      checkbox.addEventListener('change', ComparisonTableService.handleComparedItemChange.bind(ComparisonTableService));
    });

    // Cookieおよびローカルストレージに登録された商品を、比較対象追加パーツに表示。
    new ComparisonTableContainer(ComparisonTableService.getComparedItemsDetail()); //NOSONAR

    // 全商品を削除するボタンのclickイベントにイベントハンドラを登録
    const deleteAllItemsButton = document.getElementById('deleteAllItemsButton');
    if (deleteAllItemsButton) {
      deleteAllItemsButton.addEventListener('click', ComparisonTableService.handleDeleteAllItemsButtonClick.bind(ComparisonTableService));
    }

    // バリエ内選択モーダル内にあるチェックボックスのchangeイベントにイベントハンドラを登録
    const comparedItemCheckBoxesInModal = document.querySelectorAll('.js-bottomTrackingAddComparison_checkboxModal input');
    comparedItemCheckBoxesInModal.forEach(checkbox => {
      checkbox.addEventListener('change', ComparisonTableService.handleComparedItemChange.bind(ComparisonTableService));
    });

    // バリエーション選択エリアモーダルのページャー用にイベントハンドラを登録（ページ送り機能）
    document.querySelectorAll(".variationModalPageButton").forEach(pageButton => {
      pageButton.addEventListener('click', ComparisonTableService.handlePagerButtonClick.bind(ComparisonTableService));
    });

    this.setupAdobeForVariationCompareCheckBoxes();
    this.setupAdobeForCompareButtons();
    this.setupAdobeForCompareItemCheckBoxes();
  }

  /**
   * 比較表関連のエリアが動的に描き変わった際に、再初期化を行う。本メソッドはItemRefineSearchContainerから呼び出される。
   *
   * キーワード検索結果画面などで絞り込みが行われた場合、画面の再描画を伴わずに各エリアが描き変わる。
   * その場合は初期表示での初期化とは別に、再度 初期化する必要がある。
   */
  static reInit() {
    this.init(Env);
    ComparisonTableService.initDesignJS();
  }

  /**
   * 商品削除処理。この処理は比較表画面から呼ばれる。
   * @param itemKeyToRemove 削除対象の商品キー（サプライヤID-カタログ商品コード）
   */
  static removeComparedItem(itemKey: string) {
    ComparisonTableService.removeComparedItem(itemKey);
  }

  /**
   * Cookieから比較対象の商品キーを取得する。この処理は比較表画面から呼ばれる。
   * @returns 比較対象の商品キーリスト
   */
  static getItemKeysFromCookie(): string[] {
    return ComparisonTableService.getItemKeysFromCookie();
  }

  /**
   * バリエーション商品の「比較対象の選択」チェックボックが押下された際に
   * AdobeAnalyticsに集計データが送信されるようにイベントリスナを設定する。
   */
  private static setupAdobeForVariationCompareCheckBoxes() {
    // AdobeAnalyticsの初期化を行う
    new ComparisonTableAnalytics(); // NOSONAR

    const variationCompareCheckBoxes = document.querySelectorAll('.js-bottomTrackingAddComparison_modal input')
    variationCompareCheckBoxes.forEach(checkbox => {
      checkbox.addEventListener('click', () => {
        this.SHOW_COMPARISON_MODAL_EVENT.trigger({
          linkName: 'ComparisonTableAddmodal',
          target: checkbox as HTMLElement
        });
      });
    });
  }

  /**
   * 全ページ画面共通、例えば商品一覧画面などより比較表に追加にチェックを入れ、
   * 下部のモーダルより「比較する」が押下された際に
   * AdobeAnalyticsに集計データが送信されるようにイベントリスナを設定する。
   */
  private static setupAdobeForCompareButtons() {
    // AdobeAnalyticsの初期化を行う
    new ComparisonTableAddAnalytics(); // NOSONAR

    const compareButtons = document.querySelectorAll('.m-bottomTrackingAddComparison_comparisonBtn');
    compareButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.ADD_COMPARISON_EVENT.trigger({
          linkName: 'ComparisonTableAdd',
          target: button as HTMLElement
        });
      });
    });
  }

  /**
   * キーワード検索結果、カテゴリ一覧で「比較表に追加」チェックボックス押下された際に
   * AdobeAnalyticsに集計データが送信されるようにイベントリスナを設定する。
   */
  private static setupAdobeForCompareItemCheckBoxes() {
    // AdobeAnalyticsの初期化を行う
    new ComparisonTableCheckAnalytics(); // NOSONAR

    const compareItemCheckBoxes = document.querySelectorAll<HTMLInputElement>('.js-bottomTrackingAddComparison_checkbox input');
    compareItemCheckBoxes.forEach(checkbox => {
      checkbox.addEventListener('click', () => {
        if(checkbox.checked) {
          this.CHECK_COMPARISON_EVENT.trigger({
            linkName: 'ComparisonTableCheck',
            target: checkbox as HTMLElement
          });
        };
      });
    });
  }
}
